import api from "api/axios";
import type { CompanyActiveOutreach, CompanyOutreachDetail } from "./OutreachTouchesApi";

export interface CrmStage {
  id: number;
  pipeline_id: number;
  key: string;
  name: string;
  sort_order: number;
  is_terminal: boolean;
  is_won: boolean;
  is_active: boolean;
}

export interface CrmPipeline {
  id: number;
  key: string;
  name: string;
  description: string | null;
  is_active: boolean;
  stages: CrmStage[];
}

export type GatesVerdict = "PROCEED" | "PROCEED_WITH_CONDITIONS" | "HOLD_VERIFY" | "STOP";
/** Toolbar filter: "" = any, "none" = never gate-checked. */
export type GatesFilter = "" | GatesVerdict | "none";

/** Counts the list/board views get instead of the full gates blob. */
export interface GatesSummary {
  conditions: number;
  stop_reasons: number;
  to_verify: number;
  eligibility_holds: number;
  commercial_only_holds: number;
  time_critical: boolean;
  deadline_passed: boolean;
}

export interface GatesSource {
  file?: string;
  location?: string;
}

export interface GatesCondition {
  type: string;
  item?: string;
  scope?: string;
  requirement?: string;
  quote?: string;
  source?: GatesSource;
  assumption?: string;
  per_prime_check?: string;
  raise_by?: string | null;
}

export interface GatesStopReason {
  item?: string;
  quote?: string;
  source?: GatesSource;
  rule?: string;
  reason?: string;
}

export interface GatesToVerify {
  what: string;
  why?: string;
  kind?: string;
  impact?: "eligibility" | "commercial_only";
  reference?: string;
  ask?: string;
  nearest_deadline?: string | null;
  time_critical?: boolean;
}

export interface GatesNotSupplied {
  document: string;
  referenced_in?: string;
  materiality?: string;
  what_to_obtain?: string;
}

export interface GatesDiscrepancy {
  claim: string;
  doc?: string;
  kind?: string;
  original_says?: string;
}

/** The /rfp-gates result as stored on RfpDetail.gates (detail response only; every part optional). */
export interface RfpGates {
  verdict?: GatesVerdict;
  deadline_passed?: boolean;
  stop_reasons?: GatesStopReason[];
  conditions?: GatesCondition[];
  to_verify?: GatesToVerify[];
  not_supplied?: GatesNotSupplied[];
  collabp_discrepancies?: GatesDiscrepancy[];
  fetched?: { document: string; url?: string; caveat?: string }[];
  hold_resolution?: unknown;
}

export interface RfpDetail {
  id: number;
  opportunity_id: number;
  agency_name: string | null;
  solicitation_number: string | null;
  location: string | null;
  questions_deadline: string | null;
  proposal_deadline: string | null;
  budget: string | null;
  naics_codes: string[] | null;
  gates_verdict: GatesVerdict | null;
  gates_at: string | null;
  /** List/board responses only; null when the RFP was never gate-checked. */
  gates_summary?: GatesSummary | null;
  /** Detail response only. */
  gates?: RfpGates | null;
  partner: string;
  folder_name: string | null;
  folder_path: string | null;
  page_slug: string | null;
  page_url: string | null;
  page_path: string | null;
  email_file_path: string | null;
  email_subject: string | null;
  primes_csv_path: string | null;
  primes_row_count: number | null;
  page_built_at: string | null;
  email_built_at: string | null;
  primes_extracted_at: string | null;
  deployed_at: string | null;
  outreach_drafted_at?: string | null;
  outreach_started_at: string | null;
  sequence_ends_at?: string | null;
  smartlead_campaign: string | null;
  outreach_replies: number;
  outreach_opens: number;
  outreach_clicks: number;
  outreach_bounces: number;
  outreach_unsubscribes: number;
}

export interface CrmActivity {
  id: number;
  opportunity_id: number;
  actor_type: "admin" | "employee" | "cli";
  actor_id: number | null;
  activity_type: string;
  from_stage_key: string | null;
  to_stage_key: string | null;
  payload: Record<string, unknown>;
  notes: string | null;
  created_at: string | null;
}

export interface CrmOpportunity {
  id: number;
  pipeline_id: number;
  stage_id: number;
  title: string;
  source_type: string;
  description: string | null;
  custom_fields: Record<string, unknown>;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  stage_key: string;
  stage_name: string;
  stage_is_terminal: boolean;
  pipeline_key: string;
  pipeline_name: string;
  rfp: RfpDetail | null;
}

export interface CrmOpportunityDetail extends CrmOpportunity {
  activities: CrmActivity[];
}

export interface OpportunityListResponse {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: CrmOpportunity[];
}

export interface OpportunityFilters {
  pipeline_key?: string;
  stage_key?: string;
  search?: string;
  include_terminal?: boolean;
  /** One of the four verdicts, or "none" = never gate-checked. */
  gates_verdict?: GatesVerdict | "none";
  page?: number;
  page_size?: number;
}

export type RfpEventType = "deployed" | "outreach_started";

function crmApiBase(): string {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

export async function getPipeline(pipelineKey: string): Promise<CrmPipeline> {
  const res = await api.get(`${crmApiBase()}/pipelines/${pipelineKey}`);
  return res.data;
}

export async function getStageCounts(pipelineKey: string): Promise<Record<string, number>> {
  const res = await api.get(`${crmApiBase()}/pipelines/${pipelineKey}/stage-counts`);
  return res.data;
}

export async function listOpportunities(filters: OpportunityFilters = {}): Promise<OpportunityListResponse> {
  const params = new URLSearchParams();
  if (filters.pipeline_key) params.set("pipeline_key", filters.pipeline_key);
  if (filters.stage_key) params.set("stage_key", filters.stage_key);
  if (filters.search) params.set("search", filters.search);
  if (filters.include_terminal !== undefined) params.set("include_terminal", String(filters.include_terminal));
  if (filters.gates_verdict) params.set("gates_verdict", filters.gates_verdict);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 20));
  const res = await api.get(`${crmApiBase()}/opportunities?${params.toString()}`);
  return res.data;
}

export async function getOpportunity(id: number): Promise<CrmOpportunityDetail> {
  const res = await api.get(`${crmApiBase()}/opportunities/${id}`);
  return res.data;
}

export async function changeStage(id: number, stageKey: string, notes?: string): Promise<CrmOpportunity> {
  const res = await api.put(`${crmApiBase()}/opportunities/${id}/stage`, { stage_key: stageKey, notes: notes || undefined });
  return res.data;
}

export async function addNote(id: number, note: string): Promise<CrmOpportunityDetail> {
  const res = await api.post(`${crmApiBase()}/opportunities/${id}/notes`, { note });
  return res.data;
}

export async function createOpportunity(data: {
  pipeline_key: string;
  title: string;
  stage_key?: string;
  description?: string;
}): Promise<CrmOpportunity> {
  const res = await api.post(`${crmApiBase()}/opportunities`, data);
  return res.data;
}

/** What deleting an RFP would do, for the confirmation dialog. `manual` lists what a person added to a prime that would be
 *  deleted (research, reviewed emails, notes...) so it is reviewed before anything goes. */
export interface DeletePreview {
  id: number;
  title: string;
  contact_rows: number;
  company_roles: number;
  conversations?: number;
  conversation_messages?: number;
  companies_deleted: { company_id: number; name: string; manual: string[] }[];
  companies_kept: { company_id: number; name: string; reasons: string[] }[];
  refusal: { code: string; message: string } | null;
}

export interface DeleteOpportunityResult {
  deleted: boolean;
  id: number;
  title: string;
  contact_rows: number;
  company_roles: number;
  companies_deleted: { company_id: number; name: string; manual: string[] }[];
  companies_kept: { company_id: number; name: string; reasons: string[] }[];
}

/** Admin only. */
export async function getDeletePreview(id: number): Promise<DeletePreview> {
  const res = await api.get(`${crmApiBase()}/opportunities/${id}/delete-preview`);
  return res.data;
}

/** Admin only. A permanent delete: the RFP, its prime links and every prime left with no RFP (unless a do-not-contact person
 *  or a campaign lock keeps it). 409 `{code, message}` while a campaign still locks a company or a contact row has a reply. */
export async function deleteOpportunity(id: number): Promise<DeleteOpportunityResult> {
  const res = await api.delete(`${crmApiBase()}/opportunities/${id}`);
  return res.data;
}

export interface CrmAnalytics {
  pipeline_key: string;
  total_opportunities: number;
  volume_by_month: { month: string; received: number; won: number }[];
  funnel: { stage_key: string; stage_name: string; is_terminal: boolean; reached: number; current: number }[];
  win_rate_pct: number | null;
  time_in_stage: { stage_key: string; stage_name: string; avg_days: number | null; samples: number }[];
  deadline_performance: {
    campaigns_measured: number;
    avg_runway_days: number | null;
    min_runway_days: number | null;
    launched_after_deadline: number;
  };
  top_agencies: { agency: string; count: number }[];
}

export async function getAnalytics(pipelineKey = "sled_rfp"): Promise<CrmAnalytics> {
  const res = await api.get(`${crmApiBase()}/analytics`, { params: { pipeline_key: pipelineKey } });
  return res.data;
}

export async function recordRfpEvent(
  id: number,
  event: RfpEventType,
  payload: Record<string, unknown> = {}
): Promise<CrmOpportunity> {
  const res = await api.post(`/admin/crm/opportunities/${id}/rfp-events`, { event, payload });
  return res.data;
}

// ── Prime contacts (persisted us-primes.csv rows) ───────────────────────────

export interface RfpPrimeContactMessage {
  sender: "contact" | "us";
  text: string;
  at: string;
}

export interface RfpPrimeContact {
  id: number;
  opportunity_id: number;
  opportunity_title?: string; // only present on the global list, not the per-opportunity one
  // Global list only: it shows one row per person (their latest RFP).
  prime_rfp_count?: number; // distinct RFPs this person appeared on
  // Set when the person's latest reply was logged on a DIFFERENT RFP's row —
  // replied_at/reply_text/reply_thread above are then that row's, and this is
  // its id (what the reply actions must target).
  reply_contact_id?: number | null;
  is_active: boolean;
  category: string | null;
  org_name: string;
  main_contact: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  industry: string | null;
  service_type: string | null;
  linkedin_url: string | null; // this appearance's raw CSV "LinkedIn_Company_URL" snapshot — see PrimeCompany.linkedin_url for the canonical, deduplicated value
  linkedin_contacted_at: string | null;
  linkedin_status: string | null;
  // The resolved Prime's own LinkedIn profile (manually entered), joined in
  // server-side — not a raw RfpPrimeContact column. Null if unresolved or
  // never entered.
  personal_linkedin_url: string | null;
  is_high_value: boolean;
  notes: string | null;
  // Where/when this row's star was actually set — null on a row flagged
  // before this field existed, or never flagged. high_value_source_opportunity_id
  // equal to this row's own opportunity_id (or absent) means "flagged here";
  // a DIFFERENT id means it was carried forward from that other RFP.
  high_value_flagged_at: string | null;
  high_value_source_opportunity_id: number | null;
  high_value_source_opportunity_title: string | null; // joined in server-side
  replied_at: string | null;
  reply_text: string | null;
  reply_thread: RfpPrimeContactMessage[] | null; // null for rows created before reply_thread existed
  created_at: string | null;
  updated_at: string | null;
  appearance_count?: number; // only present on the global list
  // Resolved automatically at /rfp-primes sync time by email domain — null
  // when resolution failed (a generic/shared email domain never auto-links).
  prime_company_id: number | null;
  prime_contact_id: number | null;
  // The row's COMPANY'S status on THIS row's RFP (prime if any of its sheet rows is prime):
  // what the badge shows and the "Role on this RFP" filter matches.
  effective_contractor_type: ContractorTypeValue | null; // null: the company has no record for this RFP (legacy row)
  // This company's CollabP signals on this row's RFP (only on the list responses).
  plan_holder_status?: string | null;
  screen_verdict?: string | null;
  // The company's office states / countries (the State and Country filters match these, not the row's own address).
  company_states?: string[];
  company_countries?: string[];
}

export interface RfpPrimeContactFilters {
  category?: string;
  state?: string;
  country?: string; // canonical name, or "none" for rows with no country on file
  contractor_type?: ContractorTypeValue; // the company's status on the contact's own RFP
  plan_holder?: boolean; // the company is a plan holder on the contact's RFP
  has_email?: boolean;
  has_linkedin?: boolean;
  linkedin_status?: string;
  is_high_value?: boolean;
  has_replied?: boolean;
  search?: string;
  name_search?: string;
  page?: number;
  page_size?: number;
}

export interface RfpPrimeContactListResponse {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: RfpPrimeContact[];
}

export async function listRfpPrimeContacts(filters: RfpPrimeContactFilters = {}): Promise<RfpPrimeContactListResponse> {
  const params = new URLSearchParams();
  if (filters.category) params.set("category", filters.category);
  if (filters.state) params.set("state", filters.state);
  if (filters.country) params.set("country", filters.country);
  if (filters.contractor_type !== undefined) params.set("contractor_type", String(filters.contractor_type));
  if (filters.plan_holder !== undefined) params.set("plan_holder", String(filters.plan_holder));
  if (filters.has_email !== undefined) params.set("has_email", String(filters.has_email));
  if (filters.has_linkedin !== undefined) params.set("has_linkedin", String(filters.has_linkedin));
  if (filters.linkedin_status) params.set("linkedin_status", filters.linkedin_status);
  if (filters.is_high_value !== undefined) params.set("is_high_value", String(filters.is_high_value));
  if (filters.has_replied !== undefined) params.set("has_replied", String(filters.has_replied));
  if (filters.search) params.set("search", filters.search);
  if (filters.name_search) params.set("name_search", filters.name_search);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 25));
  const res = await api.get(`${crmApiBase()}/rfp-prime-contacts?${params.toString()}`);
  return res.data;
}

// Distinct countries present in the data — feeds the Country filters.
export async function listRfpPrimeContactCountries(): Promise<string[]> {
  const res = await api.get(`${crmApiBase()}/rfp-prime-contacts/countries`);
  return res.data;
}

export async function listCompanyCountries(): Promise<string[]> {
  const res = await api.get(`${crmApiBase()}/companies/countries`);
  return res.data;
}

// The canonical list the Edit Company Country dropdown offers (fetched once).
let countryOptionsCache: Promise<string[]> | null = null;
export function getCountryOptions(): Promise<string[]> {
  if (!countryOptionsCache) {
    countryOptionsCache = api
      .get(`${crmApiBase()}/countries/options`)
      .then((res) => res.data as string[])
      .catch((err) => {
        countryOptionsCache = null; // don't cache a failure
        throw err;
      });
  }
  return countryOptionsCache;
}

// Distinct office states across companies — feeds the Companies State filter.
export async function listCompanyStates(): Promise<string[]> {
  const res = await api.get(`${crmApiBase()}/companies/states`);
  return res.data;
}

export async function listRfpPrimeContactStates(): Promise<string[]> {
  const res = await api.get(`${crmApiBase()}/rfp-prime-contacts/states`);
  return res.data;
}

export async function getOpportunityRfpPrimeContacts(opportunityId: number): Promise<RfpPrimeContact[]> {
  const res = await api.get(`${crmApiBase()}/opportunities/${opportunityId}/rfp-prime-contacts`);
  return res.data;
}

export async function setRfpPrimeContactHighValue(
  contactId: number,
  isHighValue: boolean,
  notes?: string
): Promise<RfpPrimeContact> {
  const res = await api.patch(`${crmApiBase()}/rfp-prime-contacts/${contactId}`, {
    is_high_value: isHighValue,
    notes,
  });
  return res.data;
}

// Edit the contact's note, or remove it (null / empty = "reviewed, remove it" — recorded so a
// resync of the same RFP does not bring the CSV's copy back).
/** Give a person-less row (no email / a non-email value) a real email; it becomes a Prime with a profile. */
export async function setRfpPrimeContactReply(
  contactId: number,
  replied: boolean,
  replyText?: string
): Promise<RfpPrimeContact> {
  const res = await api.patch(`${crmApiBase()}/rfp-prime-contacts/${contactId}`, {
    replied,
    reply_text: replyText,
  });
  return res.data;
}

export async function addRfpPrimeContactMessage(
  contactId: number,
  sender: "contact" | "us",
  text: string,
  receivedAt?: string
): Promise<RfpPrimeContact> {
  const res = await api.post(`${crmApiBase()}/rfp-prime-contacts/${contactId}/messages`, { sender, text, ...(receivedAt ? { received_at: receivedAt } : {}) });
  return res.data;
}

// Canonical, deduplicated company — the real-world business behind however
// many RfpPrimeContact appearances (RfpPrimeContact rows) have shown up under
// it across unrelated RFPs. Auto-resolved at /rfp-primes sync time by email
// domain; capability tagging (naics_codes/country/status) lives here, not on
// an individual PrimeContact, since it's a fact about the business, not the person.
// Mirrors backend app/core/contractor_type.ContractorType.
export const ContractorType = { Sub: 1, Prime: 2, Unconfirmed: 3 } as const; // 3 = the sheet did not say; a person decides per RFP
export type ContractorTypeValue = (typeof ContractorType)[keyof typeof ContractorType];

/** Whether an office is really staffed. Only "mailing_address_only" is ignored by the local-presence test. */
export type OfficeStaffed = "staffed_office" | "mailing_address_only" | "unknown";

export interface CompanyLocation {
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  is_hq: boolean;
  source: "research" | "sheet" | "migrated";
  added_at?: string;
  staffed?: OfficeStaffed;
  // Computed by the server on every save from city / state / zip (US offices only); never sent by the client.
  lat?: number | null;
  lng?: number | null;
  geo_precision?: "zip" | "city" | "city_inferred" | null; // city_inferred: no state given, guessed from a city name found in one state only
  geo_state?: string | null; // the guessed state for city_inferred (server-computed)
  // Computed read-only by the server on read: located / not_located (fixable US office) / not_applicable (country-only or foreign).
  geo_status?: "located" | "not_located" | "not_applicable";
}

export interface LocationWarning {
  office_index: number;
  kind: "zip_state_mismatch" | "not_located";
  message: string;
}

/** One service phrase on a company. "research" = typed by a human; "sheet" = pre-filled from the CollabP sheet
 *  (unconfirmed, can never exclude a company for an RFP). */
export interface ServiceCategory {
  text: string;
  source: "research" | "sheet";
  added_at?: string;
}

/** One credential on a company: a licence, a hosting/security certificate, or (step 4a, advisory — never excluding) a bond, insurance policy, contract vehicle / registration or business certification. "has" / "lacks" (a person confirmed it does not hold it);
 *  "research" = typed or confirmed by a person, "sheet" = unconfirmed hint (never excludes). `expired` is computed
 *  server-side from `expires_on` (a "has" past its date counts as unknown in the fit check). */
export type CredentialKind = "licence" | "certificate" | "bond" | "insurance" | "vehicle" | "business_cert";
export interface Credential {
  kind: CredentialKind;
  name: string;
  status: "has" | "lacks";
  detail?: string;
  expires_on?: string | null;
  source: "research" | "sheet";
  added_at?: string;
  expired?: boolean;
  /** Bonds and insurance only, whole US dollars: single-project / per-occurrence, and aggregate. */
  amount_usd?: number | null;
  aggregate_usd?: number | null;
  /** UI only (never sent): what is currently typed in an amount box, kept as typed until it is a whole-dollar figure. */
  amount_text?: string;
  aggregate_text?: string;
}
export type CredentialsBasis = "research" | "sheet" | "reviewed_none" | "none";

/** One capability statement on a company (what it will and will not take); `label` is optional display text. */
export interface CapabilityStatement {
  text: string;
  label?: string | null;
  added_at?: string;
}

/** research: a human typed at least one; sheet: only sheet text; reviewed_none: researched, nothing found; none: never looked. */
export type ServiceBasis = "research" | "sheet" | "reviewed_none" | "none";

export interface NaicsEvidence {
  kind: "service_category" | "web";
  text: string;
  url?: string;
}

/** A NAICS code proposed for a company that has none. Only "approved" codes are in `naics_codes`; a pending one
 *  is invisible to every rfp-* lane check until a person approves it. */
export interface NaicsSuggestion {
  code: string;
  title: string | null;
  status: "pending" | "approved" | "rejected";
  source: "service_inference" | "web_research";
  confidence: "high" | "medium" | "low";
  evidence: NaicsEvidence[];
  suggested_at: string;
  decided_at: string | null;
  decided_by: string | null;
}

export interface PrimeCompany {
  id: number;
  name: string | null;
  domain: string | null; // null for a company resolved from a generic/shared email domain
  // "Other Websites": other domains this company uses (bare domains). `domain` above is the "Main Domain" —
  // usually the one contacts' emails use; a contact on either is linked to this company.
  additional_domains: string[];
  linkedin_url: string | null; // canonical company LinkedIn page — auto-filled from the primes CSV at sync time, human-editable via Edit Company thereafter
  naics_codes: string[];
  /** True only for a company added by hand with the Add company form. */
  created_manually: boolean;
  // Every known office. "sheet" entries are auto-added from the primes CSV; "research" ones are entered by hand;
  // "migrated" ones came from the old single `country`.
  locations: CompanyLocation[];
  employee_range: string | null;
  annual_revenue: number | null;
  revenue_source: string | null; // the sheet's own Revenue_Source text, or "researched"
  nigp_codes: string[];
  org_type: string | null;
  us_confirmed?: boolean; // any office in the United States (computed server-side)
  located_office_count?: number; // offices that carry coordinates (computed server-side)
  naics_suggestions?: NaicsSuggestion[];
  naics_researched_at?: string | null;
  naics_pending?: { code: string; title: string | null; confidence: string }[]; // pending suggestions (computed server-side)
  service_categories: ServiceCategory[];
  service_categories_reviewed_at: string | null;
  service_basis?: ServiceBasis; // computed server-side
  credentials?: Credential[];
  credentials_reviewed_at?: string | null;
  credentials_basis?: CredentialsBasis; // computed server-side
  capability_statements?: CapabilityStatement[];
  capability_basis?: "stated" | "reviewed_none" | "none"; // computed server-side
  // "Researched — nothing found" markers (a person looked and found none); they only mean something while the field is empty
  naics_reviewed_at?: string | null;
  capability_reviewed_at?: string | null;
  // Group C step 4b: narrative lists (same entry shape as the capability statements). Advisory only — never excludes.
  past_performance?: CapabilityStatement[];
  past_performance_basis?: "stated" | "reviewed_none" | "none";
  past_performance_reviewed_at?: string | null;
  project_size?: CapabilityStatement[];
  project_size_basis?: "stated" | "reviewed_none" | "none";
  project_size_reviewed_at?: string | null;
  domestic_performance?: CapabilityStatement[];
  domestic_performance_basis?: "stated" | "reviewed_none" | "none";
  domestic_performance_reviewed_at?: string | null;
  offices_reviewed_at?: string | null;
  status: string;
  // Set with status "not_a_fit" / "inactive" (a person's call, future RFPs only); cleared by Re-open
  status_reason?: string | null;
  status_set_by?: string | null;
  status_set_at?: string | null;
  notes: string | null;
  source_opportunity_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

// Canonical, deduplicated person — matched by exact email (primary OR
// secondary) across every appearance. Deliberately minimal:
// is_high_value/notes/reply_thread stay per-appearance (on the
// RfpPrimeContact/RfpPrimeContact row), not duplicated here — see
// PrimeCompany's comment above for why capability moved up but these
// didn't.
//
// primary_email/secondary_email/confidence/human_review_status/
// review_evidence (/rfp-primes Step 6.7, single-row model): a swap (a
// HIGH-confidence auto-swap, or a human's Verify/Reject/manual-entry
// action) updates these fields IN PLACE on this same row — never forks a
// new Prime — which is what lets status survive a swap untouched.
// confidence tells Verify/Reject whether a swap already happened (high —
// primary_email is already the candidate) or still needs to happen on
// Verify (medium/low — primary_email is still the original).
export interface PrimeContact {
  id: number;
  prime_company_id: number | null;
  primary_email: string;
  secondary_email: string | null;
  confidence: "high" | "medium" | "low" | null;
  human_review_status: "review_needed" | "verified" | "rejected" | null;
  review_evidence: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  personal_linkedin_url: string | null; // this person's own LinkedIn, manually entered — never touched by sync
  title: string | null; // job title, e.g. "Director of Procurement" — typed by hand, or filled from a sheet "Title" column
  is_primary: boolean; // the company's preferred contact: at most one per company; outreach prefers it (cleared when flagged)
  // "active" | "do_not_contact" | "bounced" — a person can only be in one
  // of these at a time. do_not_contact is a hard, person-level opt-out
  // (distinct from PrimeCompany.status's own "do_not_contact" value, which
  // is a company-wide capability concept). bounced is a hard "this exact
  // address is dead" flag. Both block outreach identically, which is why
  // they share this one field — setting either overwrites whichever was
  // there before (newest action wins). Never auto-cleared back to
  // "active"; reason/at persist as history.
  status: "active" | "do_not_contact" | "bounced";
  status_reason: string | null;
  status_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// Only present on the /prime-contacts/review-queue list response, not the plain
// Prime detail — the resolved company's display name, if linked.
export interface PrimeContactForReview extends PrimeContact {
  company_name?: string | null;
}

export interface PrimeContactWithAppearanceCount extends PrimeContact {
  appearance_count: number;
  last_replied_at?: string | null; // latest reply from this person on any active RFP (company page only)
}

/** One RFP on which somebody at the company replied (the latest reply on that RFP). */
export interface CompanyReply {
  opportunity_id: number;
  title: string | null;
  replied_at: string;
  reply_text: string | null;
  contact_email: string | null; // absent when the reply is on the campaign row of a lead with no CSV row
}

// One row per RFP this company has appeared on — prime_company_id is set
// directly on RfpPrimeContact at sync time, so contact_count counts every
// contact row from this company on that opportunity, whether or not it
// resolved to a PrimeContact.
export interface CompanyOpportunity extends CrmOpportunity {
  contact_count: number;
  // The COMPANY'S role on this RFP (prime if any of its sheet rows is prime), with
  // CollabP's signals. null = no per-RFP record (legacy data).
  contractor_type: ContractorTypeValue | null;
  plan_holder_status: string | null;
  documents_downloaded_at: string | null;
  screen_verdict: string | null;
  screen_reason: string | null;
}

/** What deleting a company would remove, counted the way the delete counts (older and inactive records included). */
export interface CompanyDeleteImpact { prime_contacts: number; rfp_contact_rows: number; rfp_roles: number; touches?: number; campaign_companies?: number; conversations?: number; conversation_messages?: number }

/** Why a delete is refused right now: a campaign that still locks the company, contacts marked Do Not Contact / Bounced,
 *  or the company's own Do Not Contact status. `blocked` is true when any applies. */
export interface CompanyDeleteBlockers {
  blocked: boolean;
  campaigns: { opportunity_id: number; title: string; locked_until: string }[];
  flagged_contacts: { email: string; status: string }[];
  company_status: string | null;
}

export interface PrimeCompanyDetail extends PrimeCompany {
  delete_impact?: CompanyDeleteImpact;
  delete_blockers?: CompanyDeleteBlockers;
  prime_contacts: PrimeContactWithAppearanceCount[];
  primary_contact_id: number | null;
  opportunities: CompanyOpportunity[];
  outreach?: CompanyOutreachDetail;
  prime_rfp_count: number; // RFPs where this company is the prime
  unconfirmed_rfp_count: number; // RFPs where its role is still unconfirmed
  sub_appearance_count: number; // RFPs where this prime appeared only as a sub-contractor (recorded, not linked)
  sub_appearances: { opportunity_id: number; title: string; first_seen_at: string | null }[];
  /** What the conversation results say about the whole company: warm (interested), teaming (already our partner) or declined (not interested at all). */
  conversation_standing?: CompanyStanding | null;
  reply_count?: number; // RFPs on which anybody at this company replied
  latest_reply_at?: string | null;
  replies?: CompanyReply[];
  us_confirmed: boolean;
}

export interface PrimeAppearance extends RfpPrimeContact {
  opportunity_title: string;
}

export interface PrimeContactDetail extends PrimeContact {
  company: PrimeCompany | null;
  is_high_value: boolean; // rolled up: true if flagged on ANY appearance
  appearances: PrimeAppearance[];
}

export interface PrimeCompanyListResponse {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: PrimeCompanyListItem[];
}

export interface CompanyStanding {
  kind: "warm" | "teaming" | "declined";
  outcome: string; // the conversation result that decides it
  outcome_at: string | null;
  opportunity_id: number;
  rfp_title: string | null;
  note: string | null;
  conversation_id: number;
}

// List rows add how many RFPs the company appeared on, by its role there.
export interface PrimeCompanyListItem extends PrimeCompany {
  prime_rfp_count: number; // RFPs where it is the prime
  unconfirmed_rfp_count: number; // RFPs where its role is still unconfirmed
  sub_appearance_count: number; // RFPs where it appeared only as a sub-contractor (recorded, not linked)
  conversation_standing?: CompanyStanding | null;
  reply_count?: number; // active RFPs on which anybody at the company replied
  latest_reply_at?: string | null;
  rfp_count: number; // distinct RFPs the company has appeared on
  last_rfp_appearance_at?: string | null; // when it was last linked to an active RFP; null = never
  countries: string[]; // distinct countries of its offices, United States first
  outreach?: CompanyActiveOutreach | null; // its active outreach (the campaign whose lock holds it), null when none
  outreach_history_count?: number; // earlier campaigns
}

/** The Edit addresses form: replaces the company's offices only; the server computes coordinates and returns
 *  warnings (a zip that belongs to another state, an office it could not place). */
export async function saveCompanyLocations(
  companyId: number,
  locations: CompanyLocation[],
  baseLocations?: CompanyLocation[],
  researchedNone = false,
): Promise<{ company: PrimeCompany; warnings: LocationWarning[] }> {
  // baseLocations = the list the form was opened on; the server refuses (409) if the offices changed meanwhile.
  const res = await api.put(`${crmApiBase()}/companies/${companyId}/locations`, { locations, base_locations: baseLocations, researched_none: researchedNone });
  return res.data;
}

/** The Edit service categories form: replaces the company's service categories only. `base` is the list the form
 *  was opened on; the server refuses (409) if it changed meanwhile. A phrase sent with source "research" confirms a
 *  sheet phrase; the server never accepts "sheet" from a client. */
export async function saveCompanyServiceCategories(
  companyId: number,
  items: ServiceCategory[],
  researchedNone: boolean,
  base?: ServiceCategory[],
): Promise<PrimeCompany> {
  const res = await api.put(`${crmApiBase()}/companies/${companyId}/service-categories`, {
    service_categories: items.map((i) => ({ text: i.text, source: i.source })),
    researched_none: researchedNone,
    base_service_categories: base,
  });
  return res.data;
}

/** The Edit credentials form: replaces the company's licences / certificates only. `base` is the list the form was
 *  opened on; the server refuses (409) if it changed meanwhile. An item sent with source "research" confirms a sheet
 *  hint; the server never accepts "sheet" from a client. */
export async function saveCompanyCredentials(
  companyId: number,
  items: Credential[],
  researchedNone: boolean,
  base?: Credential[],
): Promise<PrimeCompany> {
  const wire = (c: Credential) => ({
    kind: c.kind, name: c.name, status: c.status, detail: c.detail ?? "", expires_on: c.expires_on || null, source: c.source,
    // Amounts exist only on bonds and insurance; the server refuses them on any other kind.
    ...(c.kind === "bond" || c.kind === "insurance" ? { amount_usd: c.amount_usd ?? null, aggregate_usd: c.aggregate_usd ?? null } : {}),
  });
  const res = await api.put(`${crmApiBase()}/companies/${companyId}/credentials`, {
    credentials: items.map(wire),
    researched_none: researchedNone,
    base_credentials: base?.map(wire),
  });
  return res.data;
}

export type TextListField = "past_performance" | "project_size" | "domestic_performance";

/** The Edit past performance / project size / domestic performance forms: each replaces that one list only. `base` is
 *  the list the form was opened on; the server refuses (409) if it changed meanwhile. */
export async function saveCompanyTextList(
  companyId: number,
  field: TextListField,
  items: CapabilityStatement[],
  base?: CapabilityStatement[],
  researchedNone = false,
): Promise<PrimeCompany> {
  const wire = (i: CapabilityStatement) => ({ text: i.text, label: i.label ?? "" });
  const res = await api.put(`${crmApiBase()}/companies/${companyId}/${field.replace(/_/g, "-")}`, {
    researched_none: researchedNone,
    entries: items.map(wire),
    base_entries: base?.map(wire),
  });
  return res.data;
}

/** The Edit capability statement form: replaces the company's capability statements only. `base` is the list the
 *  form was opened on; the server refuses (409) if it changed meanwhile. */
export async function saveCompanyCapabilityStatements(
  companyId: number,
  items: CapabilityStatement[],
  base?: CapabilityStatement[],
  researchedNone = false,
): Promise<PrimeCompany> {
  const res = await api.put(`${crmApiBase()}/companies/${companyId}/capability-statements`, {
    researched_none: researchedNone,
    capability_statements: items.map((i) => ({ text: i.text, label: i.label ?? "" })),
    base_capability_statements: base?.map((i) => ({ text: i.text, label: i.label ?? "" })),
  });
  return res.data;
}

/** Mark the company's NAICS codes "researched — nothing found" (or clear the mark). The server refuses it (422) while the
 *  company has approved codes. */
export async function saveCompanyNaicsReview(companyId: number, researchedNone: boolean): Promise<PrimeCompany> {
  const res = await api.put(`${crmApiBase()}/companies/${companyId}/naics-review`, { researched_none: researchedNone });
  return res.data;
}

/** Approve or reject pending NAICS suggestions (all-or-nothing). Approving makes the code active in every lane check. */
export async function decideNaicsSuggestions(
  companyId: number,
  decisions: { code: string; decision: "approve" | "reject" }[],
): Promise<PrimeCompany> {
  const res = await api.post(`${crmApiBase()}/companies/${companyId}/naics-suggestions/decide`, { decisions });
  return res.data;
}

/** Companies with NAICS suggestions waiting for approval — feeds the same alerts as the address ones. */
export async function getNaicsPendingCount(): Promise<number> {
  const res = await api.get(`${crmApiBase()}/companies/naics-pending-count`);
  return res.data.count;
}

export const NAICS_PENDING_QUERY = "naics_pending=1";

export async function listPrimeCompanies(filters: {
  page?: number; page_size?: number; search?: string; status?: string; has_linkedin?: boolean;
  country?: string; // an office in this country ("none" = no office carries a country)
  state?: string; // an office in this state
  sort_by?: "recent_rfp_appearance"; // omitted = by name
  sort_dir?: "asc" | "desc";
  missing_address?: boolean; // no office with a street address
  office_not_located?: boolean; // a US office with a city or zip but no coordinates
  service_missing?: boolean; // nobody has researched the company's services (the research queue)
  service?: string; // a service phrase containing this text
  credentials_missing?: boolean; // nobody has researched the company's licences / certificates (the research queue)
  credential?: string; // a credential whose name or detail contains this text
  capability_missing?: boolean; // no capability statement on file and nobody marked it researched
  past_performance_missing?: boolean; // no past-performance entry on file and nobody marked it researched
  ids?: string; // comma-separated company ids — the research-gate list
  naics_pending?: boolean; // NAICS suggestions waiting for a person's approval
  manually_added?: boolean; // only companies added by hand
  has_replied?: boolean; // only companies where somebody replied
  warm?: boolean; // only companies that are warm or our partner on a live RFP
  no_rfp?: boolean; // only companies with no active link to an active RFP
  unconfirmed?: boolean; // only companies with a link whose role is still unconfirmed
} = {}): Promise<PrimeCompanyListResponse> {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.status) params.set("status", filters.status);
  if (filters.has_linkedin !== undefined) params.set("has_linkedin", String(filters.has_linkedin));
  if (filters.country) params.set("country", filters.country);
  if (filters.state) params.set("state", filters.state);
  if (filters.missing_address) params.set("missing_address", "true");
  if (filters.office_not_located) params.set("office_not_located", "true");
  if (filters.service_missing) params.set("service_missing", "true");
  if (filters.service) params.set("service", filters.service);
  if (filters.credentials_missing) params.set("credentials_missing", "true");
  if (filters.credential) params.set("credential", filters.credential);
  if (filters.capability_missing) params.set("capability_missing", "true");
  if (filters.past_performance_missing) params.set("past_performance_missing", "true");
  if (filters.ids) params.set("ids", filters.ids);
  if (filters.naics_pending) params.set("naics_pending", "true");
  if (filters.manually_added) params.set("manually_added", "true");
  if (filters.has_replied) params.set("has_replied", "true");
  if (filters.warm) params.set("warm", "true");
  if (filters.no_rfp) params.set("no_rfp", "true");
  if (filters.unconfirmed) params.set("unconfirmed", "true");
  if (filters.sort_by) {
    params.set("sort_by", filters.sort_by);
    params.set("sort_dir", filters.sort_dir ?? "desc");
  }
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 25));
  const res = await api.get(`${crmApiBase()}/companies?${params.toString()}`);
  return res.data;
}

/** Companies with no office that has a street address — feeds the Companies/Dashboard alert. */
export async function getMissingAddressCount(): Promise<number> {
  const res = await api.get(`${crmApiBase()}/companies/missing-address-count`);
  return res.data.count;
}

/** The Companies list showing only companies missing an address. Turns off the page's default
 *  "has appeared as Prime" filter, or the link would land on a narrower list than the alert counted. */
export const MISSING_ADDRESS_QUERY = "missing_address=1";

/** Companies with a US office that has an address / city / state / zip but no coordinates — feeds the same alerts. */
export async function getOfficeNotLocatedCount(): Promise<number> {
  const res = await api.get(`${crmApiBase()}/companies/office-not-located-count`);
  return res.data.count;
}

/** The Companies list showing only companies with an office that could not be placed on the map. */
export const OFFICE_NOT_LOCATED_QUERY = "office_not_located=1";

/** A human's call on a company whose role the sheet did not say, for ONE RFP. `sub` unlinks it from that RFP (the company stays). */
export async function setRoleVerdict(opportunityId: number, companyId: number, verdict: "prime" | "sub"): Promise<{ changed: boolean; verdict: "prime" | "sub"; company_name: string }> {
  const res = await api.post(`${crmApiBase()}/opportunities/${opportunityId}/role-review/${companyId}`, { verdict });
  return res.data;
}

/** Mark a company Not a fit or Inactive (future RFPs only; a reason is required; refused with 409 while a campaign locks it). */
export async function setCompanySkipStatus(companyId: number, status: "not_a_fit" | "inactive", reason: string): Promise<PrimeCompany> {
  const res = await api.post(`${crmApiBase()}/companies/${companyId}/skip-status`, { status, reason });
  return res.data;
}

/** Back to active from Not a fit / Inactive. */
export async function reopenCompany(companyId: number): Promise<PrimeCompany> {
  const res = await api.post(`${crmApiBase()}/companies/${companyId}/reopen`);
  return res.data;
}

export async function getPrimeCompany(companyId: number): Promise<PrimeCompanyDetail> {
  const res = await api.get(`${crmApiBase()}/companies/${companyId}`);
  return res.data;
}

export async function upsertPrimeCompany(data: {
  company_id?: number; email?: string; naics_codes?: string[]; name?: string; domain?: string; additional_domains?: string[];
  linkedin_url?: string; status?: string; notes?: string; source_opportunity_id?: number;
  locations?: CompanyLocation[]; employee_range?: string | null; annual_revenue?: number | null;
  revenue_source?: string | null; nigp_codes?: string[]; org_type?: string | null;
}): Promise<PrimeCompany> {
  const res = await api.post(`${crmApiBase()}/companies`, data);
  return res.data;
}

export interface CompanyDomainCheck {
  valid: boolean;
  /** The domain as it would be saved (lowercase, no www. or path); null when not valid. */
  domain: string | null;
  /** The company that already holds it, as its main domain (`main`) or one of its Other Websites (`other`). */
  owner: { company_id: number; name: string | null; domain: string | null; kind: "main" | "other" } | null;
  message: string | null;
}

/** Read-only: is this Main Domain usable? Pass the company being edited so its own domain never counts as a clash. */
export async function checkCompanyDomain(domain: string, excludeCompanyId?: number): Promise<CompanyDomainCheck> {
  const params = new URLSearchParams({ domain });
  if (excludeCompanyId !== undefined) params.set("exclude_company_id", String(excludeCompanyId));
  const res = await api.get(`${crmApiBase()}/companies/domain-check?${params.toString()}`);
  return res.data;
}

/** Add a company by hand. Name, a valid unique domain and at least one NAICS code (2 to 6 digits) are required. A second
 *  company with the same name answers 409 with `detail.code === "duplicate_name"` until `allow_duplicate_name` is sent. */
export async function createPrimeCompany(data: {
  name: string; domain: string; naics_codes: string[]; linkedin_url?: string; org_type?: string; employee_range?: string;
  allow_duplicate_name?: boolean;
}): Promise<PrimeCompany> {
  const res = await api.post(`${crmApiBase()}/companies/create`, data);
  return res.data;
}

export interface DeletePrimeCompanyResult {
  deleted: true; company_id: number; name: string | null; domain: string | null;
  prime_contacts: number; rfp_contact_rows: number; rfp_roles: number; touches?: number; campaign_companies?: number; conversations?: number; conversation_messages?: number;
}

/** Hard-delete a company with its contact people and every RFP link. There is no undo. */
export async function deletePrimeCompany(companyId: number): Promise<DeletePrimeCompanyResult> {
  const res = await api.delete(`${crmApiBase()}/companies/${companyId}`);
  return res.data;
}

// One call for a whole page of contacts, instead of one GET per row — used
// by PrimeContactsPage to badge rows whose contact resolves to a tagged
// company. Keyed by the contact's own (lowercased) email, not company id.
export async function bulkGetPrimeCompanies(emails: string[]): Promise<Record<string, PrimeCompany>> {
  if (emails.length === 0) return {};
  const res = await api.post(`${crmApiBase()}/companies/bulk`, { emails });
  return res.data.companies;
}

export async function getPrimeContact(primeId: number): Promise<PrimeContactDetail> {
  const res = await api.get(`${crmApiBase()}/prime-contacts/${primeId}`);
  return res.data;
}

export async function updatePrimeContact(primeId: number, data: {
  first_name?: string; last_name?: string; phone?: string; personal_linkedin_url?: string; title?: string | null;
}): Promise<PrimeContact> {
  const res = await api.patch(`${crmApiBase()}/prime-contacts/${primeId}`, data);
  return res.data;
}

export interface PrimeContactStatusInfo {
  // Presence in a bulkGetPrimeContactStatus result means "has status history," not
  // "currently excluded" — a present entry with status: "active" is someone
  // who WAS flagged and has since been cleared (reason kept as history).
  // Check this field, not mere presence, to know current status.
  status: "active" | "do_not_contact" | "bounced";
  reason: string | null;
  status_at: string | null;
}

/** `companyId` is only used for an email that is not on file yet (every contact belongs to a company): the server
 *  otherwise attaches the company that owns the email's domain, and answers 422 `company_required` when it has none. */
export async function setPrimeContactStatus(
  email: string, status: "active" | "do_not_contact" | "bounced", reason?: string, companyId?: number
): Promise<PrimeContact> {
  const res = await api.post(`${crmApiBase()}/prime-contacts/status`, {
    email, status, reason, company_id: companyId,
  });
  return res.data;
}

export interface NewPrimeContact {
  email: string; first_name: string; last_name?: string; title?: string; phone?: string; personal_linkedin_url?: string;
  allow_other_domain?: boolean; // the person confirmed an address outside the company's own domain
  is_primary?: boolean; // make the new contact the company's primary contact
  replace_primary?: boolean; // the person confirmed replacing the company's current primary contact (409 `primary_exists` first)
}

/** Add a contact person to a company by hand. Email + first name are required. An address outside the company's own
 *  domain answers 409 `detail.code === "email_domain_mismatch"` (with `kind`: "generic" | "other" and the `owner`
 *  company when one holds that domain) until `allow_other_domain` is sent; one already on file answers 409
 *  `email_in_use` with the existing contact's id and company. */
export async function createPrimeContact(companyId: number, data: NewPrimeContact): Promise<PrimeContact> {
  const res = await api.post(`${crmApiBase()}/companies/${companyId}/prime-contacts`, data);
  return res.data;
}

export interface SetPrimaryResult extends PrimeContact {
  replaced: { id: number; name: string; email: string } | null; // the contact that lost the mark, if any
}

/** Make a contact their company's primary contact (or take the mark off). When another contact is primary the server
 *  answers 409 `detail.code === "primary_exists"` (with that contact's `prime_contact_id`, `name`, `email`) until
 *  `replace` is sent; 409 `not_active` for a person marked Do Not Contact / Bounced. */
export async function setPrimeContactPrimary(primeContactId: number, isPrimary: boolean, replace = false): Promise<SetPrimaryResult> {
  const res = await api.put(`${crmApiBase()}/prime-contacts/${primeContactId}/primary`, { is_primary: isPrimary, replace });
  return res.data;
}

export interface ContactRemoveCheck {
  prime_contact_id: number; name: string; email: string; company_id: number; company_name: string | null;
  blocked: boolean;
  flagged: { status: string; reason: string | null } | null; // marked Do Not Contact / Bounced: set them back to active first
  campaigns: { opportunity_id: number; title: string; locked_until: string }[]; // campaigns where they are the company's last record
  message: string | null;
  impact: {
    rfp_contact_rows: number; active_rows: number; replies: number; high_value: number; was_primary: boolean;
    rfps: { id: number; title: string | null }[]; rfp_count: number;
  };
}

/** Read-only advice for the Remove dialog: would the removal be refused (and why) and what would it delete. */
export async function getContactRemoveCheck(primeContactId: number): Promise<ContactRemoveCheck> {
  const res = await api.get(`${crmApiBase()}/prime-contacts/${primeContactId}/remove-check`);
  return res.data;
}

export interface RemoveContactResult {
  deleted: true; prime_contact_id: number; email: string; name: string; company_id: number;
  rfp_contact_rows: number; replies: number; was_primary: boolean;
}

/** Hard-delete a contact person and their RFP records (the company stays). 409 `detail.code === "remove_blocked"` while
 *  they are marked Do Not Contact / Bounced or are the company's last record on a campaign that still locks it. */
export async function removePrimeContact(primeContactId: number): Promise<RemoveContactResult> {
  const res = await api.delete(`${crmApiBase()}/prime-contacts/${primeContactId}`);
  return res.data;
}

export interface PrimeContactWithoutRfp extends PrimeContact {
  company_name: string | null;
  company_domain: string | null;
}

export interface PrimeContactWithoutRfpListResponse {
  items: PrimeContactWithoutRfp[]; total_count: number; total_pages: number; page: number; page_size: number;
}

/** Contact people with no active RFP appearance (e.g. added by hand), newest first. */
export async function listPrimeContactsWithoutRfp(filters: { search?: string; page?: number; page_size?: number } = {}): Promise<PrimeContactWithoutRfpListResponse> {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 20));
  const res = await api.get(`${crmApiBase()}/prime-contacts/without-rfp?${params.toString()}`);
  return res.data;
}

// One call for a whole page of contacts, instead of one GET per row —
// looser than bulkGetPrimeCompanies' "absent means not resolved": an entry
// here can be present with status: "active" (previously flagged, since
// cleared, reason kept as history) — see PrimeContactStatusInfo.
export async function bulkGetPrimeContactStatus(emails: string[]): Promise<Record<string, PrimeContactStatusInfo>> {
  if (emails.length === 0) return {};
  const res = await api.post(`${crmApiBase()}/prime-contacts/status/bulk`, { emails });
  return res.data.status;
}

// ── Email Review Queue (/rfp-primes Step 6.7 named-email research) ──────────

export interface PrimeContactReviewFilters {
  confidence?: "high" | "medium" | "low";
  search?: string;
  page?: number;
  page_size?: number;
}

export interface PrimeContactReviewListResponse {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: PrimeContactForReview[];
}

export async function listPrimeContactsForReview(filters: PrimeContactReviewFilters = {}): Promise<PrimeContactReviewListResponse> {
  const params = new URLSearchParams();
  if (filters.confidence) params.set("confidence", filters.confidence);
  if (filters.search) params.set("search", filters.search);
  params.set("page", String(filters.page ?? 1));
  params.set("page_size", String(filters.page_size ?? 20));
  const res = await api.get(`${crmApiBase()}/prime-contacts/review-queue?${params.toString()}`);
  return res.data;
}

// Ensures primary_email = the approved address — a no-op swap for a
// confidence="high" record (already there), or performs the swap for
// medium/low (secondary_email becomes primary_email).
export async function verifyPrimeContact(primeId: number): Promise<PrimeContact> {
  const res = await api.post(`${crmApiBase()}/prime-contacts/${primeId}/verify`);
  return res.data;
}

// Ensures primary_email = the safe original — swaps back for
// confidence="high" (undoing the agent's auto-swap), a no-op for
// medium/low (primary_email was never touched).
export async function rejectPrimeContact(primeId: number): Promise<PrimeContact> {
  const res = await api.post(`${crmApiBase()}/prime-contacts/${primeId}/reject`);
  return res.data;
}

// Human-entered email, independent of whatever research proposed.
// slot="primary" bumps the current primary down to secondary and marks
// human_review_status "verified"; slot="secondary" only overwrites
// secondary_email, leaving primary/status untouched. Rejects (409) if the
// address already exists as another Prime's primary/secondary.
export async function setPrimeContactEmailManual(primeId: number, email: string, slot: "primary" | "secondary"): Promise<PrimeContact> {
  const res = await api.post(`${crmApiBase()}/prime-contacts/${primeId}/set-email`, { email, slot });
  return res.data;
}

// The companies on one RFP's primes list with their per-RFP status.
export interface RfpPrimeCompany {
  id: number;
  opportunity_id: number;
  prime_company_id: number;
  company_name: string | null;
  company_domain: string | null;
  contractor_type: ContractorTypeValue;
  contractor_type_corrected_at: string | null;
  contractor_type_corrected_by: string | null;
  plan_holder_status: string | null;
  documents_downloaded_at: string | null;
  screen_verdict: string | null;
  screen_reason: string | null;
}

export async function listRfpPrimeCompanies(opportunityId: number): Promise<RfpPrimeCompany[]> {
  const res = await api.get(`${crmApiBase()}/opportunities/${opportunityId}/prime-companies`);
  return res.data;
}
